import React, { useEffect, useState } from 'react';
import { Building, Car, LineChart, Handshake, GlobeLock, Boxes, ShieldCheck, Earth, Banknote } from 'lucide-react';
import { cn } from '../../../../../utils/classnames';
import { camelizeKeys } from '../../../../../utils/camelizeKeys';
import RealEstateTab from './propertiesTabs/RealEstateTab';
import VehicleTab from './propertiesTabs/VehicleTab';
import FinancialTab from './propertiesTabs/FinancialTab';
import SocietaryTab from './propertiesTabs/SocietaryTab';
import CryptoTab from './propertiesTabs/CryptoTab';
import OtherTab from './propertiesTabs/OtherTab';
import InsuranceTab from './propertiesTabs/InsuranceTab';
import ForeignTab from './propertiesTabs/ForeignTab';
import RetirementTab from './propertiesTabs/RetirementTab';

const PropertiesTab = ({ clientId, scope, onSuccess, canWrite }) => {
  const [activeTab, setActiveTab] = useState(() => {
    const urlParams = new URLSearchParams(window.location.search);
    return urlParams.get('subTab') || 'real_estate';
  });
  const [hoveredTab, setHoveredTab] = useState(null);
  const [propertiesCounts, setPropertiesCounts] = useState({
    realEstatePropertiesCount: 0,
    vehiclePropertiesCount: 0,
    financialPropertiesCount: 0,
    societaryPropertiesCount: 0,
    cryptoPropertiesCount: 0,
    otherPropertiesCount: 0,
    insurancePropertiesCount: 0,
    foreignPropertiesCount: 0,
    retirementPropertiesCount: 0,
  });

  const tabs = [
    { key: 'real_estate', label: 'Imóveis', icon: Building, countKey: 'realEstatePropertiesCount' },
    { key: 'vehicle', label: 'Veículos', icon: Car, countKey: 'vehiclePropertiesCount' },
    { key: 'financial', label: 'Saldos e Investimentos', icon: LineChart, countKey: 'financialPropertiesCount' },
    { key: 'retirement', label: 'Previdência', icon: Banknote, countKey: 'retirementPropertiesCount' },
    { key: 'societary', label: 'Participações societárias', icon: Handshake, countKey: 'societaryPropertiesCount' },
    { key: 'crypto', label: 'Criptoativos', icon: GlobeLock, countKey: 'cryptoPropertiesCount' },
    { key: 'other', label: 'Outros bens', icon: Boxes, countKey: 'otherPropertiesCount' },
    { key: 'insurance', label: 'Seguros', icon: ShieldCheck, countKey: 'insurancePropertiesCount' },
    { key: 'foreign', label: 'Bens no exterior', icon: Earth, countKey: 'foreignPropertiesCount' },
  ];

  useEffect(() => {
    loadPropertiesCount();
  }, [clientId]);

  const loadPropertiesCount = () => {
    fetch(`/app/planner/client/${clientId}/dashboard/properties?scope=${scope}`)
      .then((res) => res.json())
      .then((data) => {
        const camelData = camelizeKeys(data);
        setPropertiesCounts(camelData.propertiesCounts);
      });
  };

  const handleTabChange = (key) => {
    setActiveTab(key);
    const url = new URL(window.location);
    url.searchParams.set('subTab', key);
    window.history.pushState({}, '', url);
  };

  return (
    <div className="grid grid-cols-5 gap-5">
      {/* Navbar */}
      <div className="col-span-1 py-5 rounded-custom20 bg-white">
        <ul className="flex flex-col">
          {tabs.map((tab, index) => {
            const isActive = activeTab === tab.key;
            const isHovered = hoveredTab === tab.key;

            const isCurrentActiveOrHovered = isActive || isHovered;
            const isPrevActiveOrHovered =
              index > 0 && (activeTab === tabs[index - 1].key || hoveredTab === tabs[index - 1].key);

            const shouldRenderHr = index > 0;
            const hrColor = isCurrentActiveOrHovered || isPrevActiveOrHovered ? 'bg-white' : 'bg-blueWhisper';

            return (
              <React.Fragment key={tab.key}>
                {shouldRenderHr && <hr className={cn('mx-5 h-px border-0', hrColor)} />}

                <li
                  className={cn(isActive ? 'bg-blueExtraUltraLight' : 'hover:bg-gray-100')}
                  onClick={() => handleTabChange(tab.key)}
                  onMouseEnter={() => setHoveredTab(tab.key)}
                  onMouseLeave={() => setHoveredTab(null)}
                >
                  <div className="flex items-center gap-3 px-5 pt-5 py-4 cursor-pointer text-base font-medium text-blueDarker">
                    {tab.icon &&
                      React.createElement(tab.icon, {
                        className: 'text-tealMedium',
                        size: 18,
                        strokeWidth: 2,
                      })}
                    <span>{tab.label}</span>
                    {propertiesCounts[tab.countKey] > 0 && (
                      <span className="w-custom18 h-custom18 flex items-center justify-center rounded-full bg-blueDark text-white text-xxs font-semibold">
                        {propertiesCounts[tab.countKey]}
                      </span>
                    )}
                  </div>
                </li>
              </React.Fragment>
            );
          })}
        </ul>
      </div>

      {/* Content */}
      <div className="col-span-4 bg-white rounded-custom20 p-5">
        {activeTab === 'real_estate' && <RealEstateTab clientId={clientId} scope={scope} canWrite={canWrite} />}
        {activeTab === 'vehicle' && <VehicleTab clientId={clientId} scope={scope} canWrite={canWrite} />}
        {activeTab === 'financial' && <FinancialTab clientId={clientId} scope={scope} canWrite={canWrite} />}
        {activeTab === 'societary' && <SocietaryTab clientId={clientId} scope={scope} canWrite={canWrite} />}
        {activeTab === 'crypto' && <CryptoTab clientId={clientId} scope={scope} canWrite={canWrite} />}
        {activeTab === 'other' && <OtherTab clientId={clientId} scope={scope} canWrite={canWrite} />}
        {activeTab === 'insurance' && <InsuranceTab clientId={clientId} scope={scope} canWrite={canWrite} />}
        {activeTab === 'foreign' && <ForeignTab clientId={clientId} scope={scope} canWrite={canWrite} />}
        {activeTab === 'retirement' && <RetirementTab clientId={clientId} scope={scope} canWrite={canWrite} />}
      </div>
    </div>
  );
};

export default PropertiesTab;
